import React, { useEffect, useState } from 'react';
import SubCourseListing from './SubCourseListing';
import { columnTypes } from 'src/components/UI/atoms/ATMTable/ATMTable';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'src/redux/store';
import {
  setPage, setRowsPerPage, setSearchValue, setItems,
  setTotalItems,
  setIsTableLoading
} from 'src/redux/slices/SubCourseSlice';
import AddSubCourseFormWrapper from '../Add/AddSubCourseFormWrapper';
import EditSubCourseFormWrapper from '../Edit/EditSubCourseFormWrapper';
import { MdDeleteOutline } from 'react-icons/md';
import { RxPencil1 } from 'react-icons/rx';
import { showConfirmationDialog } from 'src/utils/showConfirmationDialog';
import ATMMenu from 'src/components/UI/atoms/ATMMenu/ATMMenu';
import { useDeleteSubCourseMutation, useGetSubCoursePaginationQuery } from 'src/services/CourseServices';
import { showToast } from 'src/utils/showToaster';
import { useParams } from 'react-router-dom';

type Props = {};

const SubCourseListingWrapper = (props: Props) => {
  const dispatch = useDispatch();
  const { courseId } = useParams();
  const [isOpenAddForm, setIsOpenAddForm] = useState(false);
  const [isOpenEditForm, setIsOpenEditForm] = useState(false);
  const [subCourseId, setSubCourseId] = useState("");
  const [deleteSubCourse] = useDeleteSubCourseMutation();

  const { items, totalItems, page, rowsPerPage, searchValue, isTableLoading } =
    useSelector((state: RootState) => state.subCourse);

  const { data, isLoading, isFetching } = useGetSubCoursePaginationQuery({
    limit: rowsPerPage,
    searchValue: searchValue,
    params: ["name"],
    page: page,
    filterBy: [{
      fieldName:'courseId',
      value :[courseId]
    }],
    dateFilter: {},
    orderBy: "createdAt",
    orderByValue: -1,
    isPaginationRequired: true,
  });

  useEffect(() => {
    if (!isLoading && !isFetching) {
      dispatch(setItems(data?.data || []));
      dispatch(setTotalItems(data?.totalItem || 0));
      dispatch(setRowsPerPage(data?.pageSize || 0));
      dispatch(setPage(data?.currentPage || 0));
      dispatch(setIsTableLoading(false));
    } else {
      dispatch(setIsTableLoading(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isLoading, isFetching]);

  const getActionOptions = (courseId: string) => {
    return [
      {
        label: (
          <div className="flex gap-2 items-center text-secondary-main">
            <RxPencil1 className="text-lg" /> Edit
          </div>
        ),
        onClick: () => {
          setIsOpenEditForm(true)
          setSubCourseId(courseId) 
        },

      },
      {
        label: (
          <div className="flex gap-2 items-center text-red-600 font-semibold">
            <MdDeleteOutline className="text-lg" /> Delete
          </div>
        ),
        onClick: () => {
          showConfirmationDialog({
            title: "Heads Up",
            text: "Are you sure, you want to Delete this Sub-Course ?",
            icon: "question",
            showCancelButton: true,
            next: (result) => {
              if (result.isConfirmed) {
                deleteSubCourse(courseId).then((res: any) => {
                  if (res?.error) {
                    showToast("error", res?.error?.data?.message);
                  } else {
                    if (res?.data?.status) {
                      showToast("success", res?.data?.message);
                    } else {
                      showToast("error", res?.data?.message);
                    }
                  }
                });
              }
            },
          });
        },

      },
    ];
  };

  const columns: columnTypes[] = [
    {
      field: 'name',
      headerName: 'Sub-Course',
      flex: 'flex-[1_1_0%]',
      extraClasses : 'capitalize'
    },
   
    {
      field: "action",
      headerName: "Action",
      flex: "flex-[0.25_0.25_0%]",
      extraClasses: "",
      renderCell: (row: any) => {
        const options = getActionOptions(row?._id);
        return <ATMMenu options={options} orientation="vertical" />;
      },
    },
  ];

  return (
    <div>
      <SubCourseListing
        columns={columns}
        rows={items}
        onAddNew={() => setIsOpenAddForm(true)}
        paginationProps={{
          isTableLoading,
          totalItems,
          page,
          rowsPerPage,
          searchValue,
          setPage: (newPage) => dispatch(setPage(newPage)),
          setRowsPerPage: (newRowsPerPage) => dispatch(setRowsPerPage(newRowsPerPage)),
          setSearchValue: (newValue) => dispatch(setSearchValue(newValue)),
        }}
      />
      {isOpenAddForm ? (
        <AddSubCourseFormWrapper onClose={() => setIsOpenAddForm(false)} />
      ) : null}
      {isOpenEditForm ? (
        <EditSubCourseFormWrapper
          onClose={() => setIsOpenEditForm(false)}
          subCourseId ={subCourseId}
        />
      ) : null}
    </div>
  );
};

export default SubCourseListingWrapper;